import { forwardRef, memo } from 'react';
import {
  CalendarIcon,
  DocumentTextIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

import { ExclamationCircleIcon } from '@heroicons/react/16/solid';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { Badge } from '@/components/ui/daisy/badge';
import { Card } from '@/components/ui/daisy/card';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { ScheduledLessonCardProps, ScheduledLessonCardVariant } from './types';

const containerClasses: Record<ScheduledLessonCardVariant, string> = {
  default: '',
  caution: 'border border-error',
};

const titleClasses: Record<ScheduledLessonCardVariant, string> = {
  default: 'text-base-content',
  caution: 'text-error',
};

const ScheduledLessonCardBase = forwardRef<
  HTMLDivElement,
  ScheduledLessonCardProps
>(
  (
    {
      name,
      startDate,
      endDate,
      studentsCount,
      attendancesCount,
      examsCount,
      variant = 'default',
      className,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation('translation', {
      keyPrefix: 'common.scheduledLessonCard',
    });

    const endsSoon = moment(endDate).isBefore(moment().add(2, 'weeks'));

    return (
      <Card
        ref={ref}
        size="xs"
        className={twMerge(
          'rounded-2xl bg-base-200 drop-shadow',
          containerClasses[variant],
          className,
        )}
        {...props}
      >
        <Card.Body className="gap-4">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Badge size="xs" variant="soft" color="neutral">
                {t('time', { time: moment(startDate).format('HH:mm') })}
              </Badge>
              <Badge
                size="xs"
                variant="soft"
                color="secondary"
                aria-label={t('students', { count: studentsCount })}
              >
                <UsersIcon className="size-2.5" />
                {studentsCount}
              </Badge>
            </div>
            <Card.Title
              className={twMerge(
                'text-xs font-semibold leading-4 line-clamp-2',
                titleClasses[variant],
              )}
            >
              {name}
            </Card.Title>
          </div>
          <Badge
            size="xs"
            variant="soft"
            color={endsSoon ? 'warning' : 'accent'}
            className="w-fit self-center"
          >
            {t('endsAt', { date: moment(endDate).format('DD/MM/YY') })}
          </Badge>
          <div className="flex items-center gap-1">
            <Badge
              size="xs"
              variant="soft"
              color="success"
              aria-label={t('attendances', { count: attendancesCount })}
            >
              <CalendarIcon className="size-2.5" />
              {attendancesCount}
            </Badge>
            <Badge
              size="xs"
              variant="soft"
              color="warning"
              aria-label={t('exams', { count: examsCount })}
            >
              <DocumentTextIcon className="size-2.5" />
              {examsCount}
            </Badge>
            <ConditionalRenderer condition={variant === 'caution'}>
              <ExclamationCircleIcon
                role="img"
                aria-label={t('requiresAttention')}
                className="size-3 text-error"
              />
            </ConditionalRenderer>
          </div>
        </Card.Body>
      </Card>
    );
  },
);

ScheduledLessonCardBase.displayName = 'ScheduledLessonCard';

export const ScheduledLessonCard = memo(ScheduledLessonCardBase);
